import { useEffect, useState } from "react";
import { useCompany } from "@/context/CompanyContext";
import api, { formatINR } from "@/lib/api";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import {
  TrendingUp, TrendingDown, Wallet, Landmark,
  ArrowDownCircle, ArrowUpCircle, LineChart as LineIcon, Receipt,
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

// Stable references — extracted to module scope to avoid recreating on every render.
const AXIS_TICK = { fontSize: 11 };
const TOOLTIP_STYLE = { fontSize: 12, fontFamily: "IBM Plex Mono" };
const LINE_DOT = { r: 3 };
const tooltipFormatter = (v) => `₹ ${formatINR(v)}`;

const KpiCard = ({ icon: Icon, label, value, tone = "default", sub }) => {
  const toneMap = {
    default: "bg-blue-50 text-blue-700",
    success: "bg-emerald-50 text-emerald-700",
    danger: "bg-red-50 text-red-700",
    neutral: "bg-gray-100 text-gray-700",
  };
  return (
    <div className="border border-gray-200 bg-white rounded p-4">
      <div className="flex items-start justify-between">
        <div className={`w-8 h-8 rounded flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
        {sub && <span className="text-[10px] uppercase tracking-wider text-gray-400">{sub}</span>}
      </div>
      <div className="mt-3 text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 font-mono text-xl font-semibold text-gray-900 tabular-nums">
        ₹ {formatINR(value)}
      </div>
    </div>
  );
};

function Inner() {
  const { activeId, active } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/companies/${activeId}/dashboard`);
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeId]);

  return (
    <>
      <PageHeader
        title={`Dashboard — ${active?.name || ""}`}
        subtitle={loading ? "Loading…" : "Real-time snapshot of your books"}
      />
      <div className="p-6 space-y-6" data-testid="dashboard-page">
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <KpiCard icon={TrendingUp} label="Today's Sales" value={data.today_sales} tone="success" sub="Today" />
              <KpiCard icon={TrendingDown} label="Today's Purchases" value={data.today_purchases} tone="danger" sub="Today" />
              <KpiCard icon={Wallet} label="Cash in Hand" value={data.cash_balance} tone="neutral" />
              <KpiCard icon={Landmark} label="Bank Balance" value={data.bank_balance} tone="default" />
              <KpiCard icon={ArrowDownCircle} label="Receivables" value={data.receivables} tone="success" />
              <KpiCard icon={ArrowUpCircle} label="Payables" value={data.payables} tone="danger" />
              <KpiCard icon={LineIcon} label="Net Profit" value={data.net_profit} tone={data.net_profit >= 0 ? "success" : "danger"} />
              <KpiCard icon={Receipt} label="Total Sales" value={data.total_sales} tone="neutral" sub="YTD" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 border border-gray-200 bg-white rounded p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Sales Trend</h3>
                  <span className="text-xs text-gray-500">Last 7 entries</span>
                </div>
                <div className="h-60">
                  {data.sales_trend.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-sm text-gray-400">
                      No sales recorded yet
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.sales_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="date" tick={AXIS_TICK} stroke="#94a3b8" />
                        <YAxis tick={AXIS_TICK} stroke="#94a3b8" />
                        <Tooltip
                          formatter={tooltipFormatter}
                          contentStyle={TOOLTIP_STYLE}
                        />
                        <Line type="monotone" dataKey="amount" stroke="#1d4ed8" strokeWidth={2} dot={LINE_DOT} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="border border-gray-200 bg-white rounded p-4">
                <h3 className="font-medium text-gray-900 mb-3">At a glance</h3>
                <ul className="text-sm space-y-2">
                  <li className="flex items-center justify-between">
                    <span className="text-gray-600">Ledgers</span>
                    <span className="font-mono">{data.ledger_count}</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-gray-600">Vouchers</span>
                    <span className="font-mono">{data.voucher_count}</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="text-gray-600">Total Purchases</span>
                    <span className="font-mono">₹ {formatINR(data.total_purchases)}</span>
                  </li>
                </ul>
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Quick entry</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {[
                      ["F5", "Payment"],
                      ["F6", "Receipt"],
                      ["F8", "Sales"],
                      ["F9", "Purchase"],
                    ].map(([k, l]) => (
                      <div key={k} className="flex items-center gap-2 border border-gray-200 rounded px-2 py-1.5">
                        <kbd className="tx-kbd">{k}</kbd>
                        <span>{l}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default function Dashboard() {
  return <RequireCompany><Inner /></RequireCompany>;
}
